import React, { useState, useEffect, useRef } from 'react';
import { useConfigStore } from '../store/configStore';

import axiosInstance from '../lib/axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Key, RefreshCw, CheckCircle2 } from 'lucide-react';
import ByokPaywall from '../../components/ByokPaywall';



const ConfigPanel = () => {
  const { apiKey, model, imageModel, describeModel, setApiKey, setModel, setImageModel, setDescribeModel } = useConfigStore();

  const [models, setModels] = useState([]);
  const [loadingModels, setLoadingModels] = useState(false);
  const [tempApiKey, setTempApiKey] = useState(apiKey);
  const [tempModel, setTempModel] = useState(model);
  const [tempImageModel, setTempImageModel] = useState(imageModel);
  const [tempDescribeModel, setTempDescribeModel] = useState(describeModel);
  const [modelFilter, setModelFilter] = useState('');
  const [imageModelFilter, setImageModelFilter] = useState('');
  const [describeModelFilter, setDescribeModelFilter] = useState('');
  const [isTesting, setIsTesting] = useState(false);
  const filterRef = useRef(null);
  const imageFilterRef = useRef(null);
  const describeFilterRef = useRef(null);


  useEffect(() => {
    setTempApiKey(apiKey);
    setTempModel(model);
    setTempImageModel(imageModel);
    setTempDescribeModel(describeModel);
  }, [apiKey, model, imageModel, describeModel]);
  // Cargar modelos automáticamente al abrir el panel
  useEffect(() => {
    if (models.length === 0) {
      fetchModels();
    }

  }, []);


  const fetchModels = async () => {
    setLoadingModels(true);
    try {
      const response = await axiosInstance.get('/ai/models');
      setModels(response.data.models || []);
    } catch (error) {
      toast.error('Error al cargar modelos');
    } finally {
      setLoadingModels(false);
    }
  };

  const handleTestModel = async () => {
    if (!tempApiKey?.trim()) {
      toast.error('Configura tu API Key primero');
      return;
    }
    if (!tempModel?.trim()) {
      toast.error('Selecciona un modelo');
      return;
    }
    setIsTesting(true);
    try {
      const res = await axiosInstance.post('/ai/test-model', {
        api_key: tempApiKey, model: tempModel,
      });
      const ms = res.data?.latency_ms ?? undefined;
      toast.success(ms ? `Modelo OK (${ms} ms)` : 'Modelo OK');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al probar el modelo');
    } finally {
      setIsTesting(false);
    }
  };

  const handleSave = () => {
    // T-037: saving without an API key is allowed — it clears the BYOK key and
    // switches to platform mode (the admin's key + default models are used).
    setApiKey(tempApiKey);
    setModel(tempModel);
    setImageModel(tempImageModel);
    setDescribeModel(tempDescribeModel);
    toast.success(tempApiKey.trim() ? 'Configuración guardada' : 'Configuración guardada (modo plataforma, sin llave BYOK)');
  };

  // T-037: model selectors are BYOK-gated (UX only; the server-side guarantee is
  // that without a BYOK key the admin defaults win regardless).
  const modelsDisabled = !tempApiKey?.trim();

  const filteredModels = models.filter(m =>
    (m.name || m.id).toLowerCase().includes(modelFilter.toLowerCase())
  );

  // /ai/models carries no reliable image-capability flag, so we reuse the full
  // list with its own filter box; the default image model is preselected.
  const filteredImageModels = models.filter(m =>
    (m.name || m.id).toLowerCase().includes(imageModelFilter.toLowerCase())
  );

  // T-036: describe/vision model reuses the same list + its own filter box.
  const filteredDescribeModels = models.filter(m =>
    (m.name || m.id).toLowerCase().includes(describeModelFilter.toLowerCase())
  );

  // T-087: BYOK (llave propia + modelos) es beneficio del plan mensual — todo
  // el panel queda tras el paywall si no hay plan activo.
  return (
    <ByokPaywall>
    <div className="space-y-6">
      {/* API Key */}
      <div className="space-y-2">
        <Label htmlFor="api-key" className="flex items-center gap-2">
          <Key className="w-4 h-4" />
          OpenRouter API Key
        </Label>
        <Input
          id="api-key"
          data-testid="api-key-input"
          type="password"
          placeholder="sk-or-v1-..."
          value={tempApiKey}
          onChange={(e) => setTempApiKey(e.target.value)}
          className="font-mono"
        />
        <p className="text-xs text-slate-500 dark:text-slate-400">
          Obtén tu API Key en{' '}
          <a
            href="https://openrouter.ai/keys"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sky-600 dark:text-sky-400 hover:underline"
          >
            openrouter.ai/keys
          </a>
        </p>
        {modelsDisabled && (
          <p className="text-xs text-amber-600 dark:text-amber-400">
            Sin llave BYOK se usan los modelos del administrador.
          </p>
        )}
      </div>

      {/* Model Selection */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="model">Modelo</Label>
          <div className="flex items-center gap-2">
            <Button
              data-testid="refresh-models-button"
              variant="ghost"
              size="sm"
              onClick={fetchModels}
              disabled={loadingModels}
              title="Actualizar lista de modelos"
            >
              <RefreshCw className={`w-4 h-4 ${loadingModels ? 'animate-spin' : ''}`} />
            </Button>
            <Button
              data-testid="test-model-button"
              variant="secondary"
              size="sm"
              onClick={handleTestModel}
              disabled={isTesting || !tempApiKey?.trim() || !tempModel?.trim()}
              title="Probar modelo seleccionado"
            >
              <CheckCircle2 className={`w-4 h-4 ${isTesting ? 'animate-pulse' : ''}`} />
            </Button>
          </div>
        </div>

        {models.length > 0 ? (
          <Select disabled={modelsDisabled} value={tempModel} onValueChange={setTempModel} onOpenChange={(open) => { if (open && models.length === 0) { fetchModels(); } setTimeout(() => filterRef?.current?.focus(), 0); }}>
            <SelectTrigger data-testid="model-select">
              <SelectValue placeholder="Selecciona un modelo" />
            </SelectTrigger>
            <SelectContent className="max-h-[400px]">
              <div className="sticky top-0 bg-popover z-10 p-2 border-b">
                <Input
                  data-testid="model-filter-input"
                  placeholder="Buscar modelo..."
                  value={modelFilter}
                  ref={filterRef}
                  onChange={(e) => { setModelFilter(e.target.value); setTimeout(() => filterRef?.current?.focus(), 0); }}
                  className="text-sm h-8"
                  onPointerDown={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                />
              </div>
              <div className="overflow-y-auto max-h-[300px]">
                {filteredModels.length > 0 ? (
                  filteredModels.slice(0, 100).map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.name || m.id}
                    </SelectItem>
                  ))
                ) : (
                  <div className="p-4 text-sm text-slate-500 dark:text-slate-400 text-center">
                    No se encontraron modelos con "{modelFilter}"
                  </div>
                )}
              </div>
            </SelectContent>
          </Select>
        ) : (
          <Input
            id="model"
            data-testid="model-input"
            placeholder="openai/gpt-4o-mini"
            value={tempModel}
            onChange={(e) => setTempModel(e.target.value)}
            disabled={modelsDisabled}
          />
        )}
        <p className="text-xs text-slate-500 dark:text-slate-400">
          Ver modelos disponibles en{' '}
          <a
            href="https://openrouter.ai/models"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sky-600 dark:text-sky-400 hover:underline"
          >
            openrouter.ai/models
          </a>
        </p>
      </div>

      {/* Image Generation Model (T-031) */}
      <div className="space-y-2">
        <Label htmlFor="image-model">Modelo de generación de imágenes</Label>
        {models.length > 0 ? (
          <Select disabled={modelsDisabled} value={tempImageModel} onValueChange={setTempImageModel} onOpenChange={(open) => { if (open && models.length === 0) { fetchModels(); } setTimeout(() => imageFilterRef?.current?.focus(), 0); }}>
            <SelectTrigger data-testid="image-model-select">
              <SelectValue placeholder="Selecciona un modelo de imagen" />
            </SelectTrigger>
            <SelectContent className="max-h-[400px]">
              <div className="sticky top-0 bg-popover z-10 p-2 border-b">
                <Input
                  data-testid="image-model-filter-input"
                  placeholder="Buscar modelo..."
                  value={imageModelFilter}
                  ref={imageFilterRef}
                  onChange={(e) => { setImageModelFilter(e.target.value); setTimeout(() => imageFilterRef?.current?.focus(), 0); }}
                  className="text-sm h-8"
                  onPointerDown={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                />
              </div>
              <div className="overflow-y-auto max-h-[300px]">
                {filteredImageModels.length > 0 ? (
                  filteredImageModels.slice(0, 100).map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.name || m.id}
                    </SelectItem>
                  ))
                ) : (
                  <div className="p-4 text-sm text-slate-500 dark:text-slate-400 text-center">
                    No se encontraron modelos con "{imageModelFilter}"
                  </div>
                )}
              </div>
            </SelectContent>
          </Select>
        ) : (
          <Input
            id="image-model"
            data-testid="image-model-input"
            placeholder="google/gemini-2.5-flash-image-preview"
            value={tempImageModel}
            onChange={(e) => setTempImageModel(e.target.value)}
            disabled={modelsDisabled}
          />
        )}
        <p className="text-xs text-slate-500 dark:text-slate-400">
          Usado por «Generar imagen» y la acción rápida de imagen.
        </p>
      </div>

      {/* Image Description / Vision Model (T-036) */}
      <div className="space-y-2">
        <Label htmlFor="describe-model">Modelo de descripción de imágenes</Label>
        {models.length > 0 ? (
          <Select disabled={modelsDisabled} value={tempDescribeModel} onValueChange={setTempDescribeModel} onOpenChange={(open) => { if (open && models.length === 0) { fetchModels(); } setTimeout(() => describeFilterRef?.current?.focus(), 0); }}>
            <SelectTrigger data-testid="describe-model-select">
              <SelectValue placeholder="Selecciona un modelo de visión" />
            </SelectTrigger>
            <SelectContent className="max-h-[400px]">
              <div className="sticky top-0 bg-popover z-10 p-2 border-b">
                <Input
                  data-testid="describe-model-filter-input"
                  placeholder="Buscar modelo..."
                  value={describeModelFilter}
                  ref={describeFilterRef}
                  onChange={(e) => { setDescribeModelFilter(e.target.value); setTimeout(() => describeFilterRef?.current?.focus(), 0); }}
                  className="text-sm h-8"
                  onPointerDown={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                />
              </div>
              <div className="overflow-y-auto max-h-[300px]">
                {filteredDescribeModels.length > 0 ? (
                  filteredDescribeModels.slice(0, 100).map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.name || m.id}
                    </SelectItem>
                  ))
                ) : (
                  <div className="p-4 text-sm text-slate-500 dark:text-slate-400 text-center">
                    No se encontraron modelos con "{describeModelFilter}"
                  </div>
                )}
              </div>
            </SelectContent>
          </Select>
        ) : (
          <Input
            id="describe-model"
            data-testid="describe-model-input"
            placeholder="google/gemini-2.5-flash"
            value={tempDescribeModel}
            onChange={(e) => setTempDescribeModel(e.target.value)}
            disabled={modelsDisabled}
          />
        )}
        <p className="text-xs text-slate-500 dark:text-slate-400">
          Usado por «Describir imagen». Debe ser un modelo con capacidad de visión.
        </p>
      </div>

      {/* T-081: el historial del chat ya NO es global — se elige por conversación
          en el panel del chat, junto al selector de contexto. */}

      {/* Save Button */}
      <Button
        data-testid="save-config-button"
        onClick={handleSave}
        className="w-full bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700"
      >
        Guardar Configuración
      </Button>
    </div>
    </ByokPaywall>
  );
};

export default ConfigPanel;
